import ApplyConversationActionContext from 'owa-mail-store/lib/store/schema/ApplyConversationActionContext';
import ApplyConversationActionRequest from 'owa-service/lib/contract/ApplyConversationActionRequest';
import ApplyConversationActionResponseMessage from 'owa-service/lib/contract/ApplyConversationActionResponseMessage';
import ConversationAction from 'owa-service/lib/contract/ConversationAction';
import applyConversationActionRequest from 'owa-service/lib/factory/applyConversationActionRequest';
import conversationAction from 'owa-service/lib/factory/conversationAction';
import folderIdFactory from 'owa-service/lib/factory/folderId';
import itemId from 'owa-service/lib/factory/itemId';
import targetFolderIdFactory from 'owa-service/lib/factory/targetFolderId';
import applyConversationActionOperation from 'owa-service/lib/operation/applyConversationActionOperation';
import { getJsonRequestHeader } from 'owa-service/lib/Utils';
import InferenceClassification from 'owa-service/lib/contract/InferenceClassification';
import getEwsRequestDateString from 'owa-mail-store/lib/services/utils/getEwsRequestDateString';

/**
 * Create the update hashtag conversationAction for the given conversation
 * @param conversationContext the conversation context to be flagged
 * @param hashtag the hashtag type to apply
 * @param hashtagBase hashtagBase to apply
 * @param folderId the sourceFolderId
 * @param inferenceClassification the inferenceClassification to apply
 * @returns the generated ConversationAction
 */
function createUpdateHashtagConversationAction(
    conversationContext: ApplyConversationActionContext,
    hashtag: string,
    hashtagBase: string,
    folderId: string,
    inferenceClassification?: InferenceClassification
): ConversationAction {
    const updatHashtagsAction = conversationAction({
        Action: 'SetInboxTabClassificationState',
        ConversationId: itemId({ Id: conversationContext.conversationId }),
        NewHashtagValues: hashtag === '' ? [] : [hashtag],
        PriorHashtagValues: [hashtagBase],
        ConversationLastSyncTime: getEwsRequestDateString(
            conversationContext.conversationLastSyncTimeStamp
        ),
    });

    if (inferenceClassification) {
        updatHashtagsAction.InferenceClassification = inferenceClassification;
    }

    // folderId can be null in search mode.
    if (folderId) {
        const targetFolderId = targetFolderIdFactory({
            BaseFolderId: folderIdFactory({
                Id: folderId,
            }),
        });

        updatHashtagsAction.ContextFolderId = targetFolderId;
    }

    return updatHashtagsAction;
}

/**
 * Configure the request body
 * @param conversationIds the conversation ids
 * @param hashtag the hashtag type to apply
 * @param hashtagBase hashtagBase to apply
 * @param folderId the sourceFolderId
 * @param inferenceClassification the inferenceClassification to apply
 * @returns an ApplyConversationActionRequest
 */
function configureRequestBody(
    conversationContexts: ApplyConversationActionContext[],
    hashtag: string,
    hashtagBase: string,
    folderId: string,
    inferenceClassification?: InferenceClassification
): ApplyConversationActionRequest {
    const conversationActions = conversationContexts.map(conversationContext =>
        createUpdateHashtagConversationAction(
            conversationContext,
            hashtag,
            hashtagBase,
            folderId,
            inferenceClassification
        )
    );

    return applyConversationActionRequest({
        ConversationActions: conversationActions,
    });
}

/**
 * Sends out the service request to set hashtag properties on conversation
 * @param conversationContexts the conversation contexts to be deleted
 * @param hashtag the hashtag type to apply
 * @param hashtagBase hashtagBase to apply
 * @param folderId the sourceFolderId
 * @param inferenceClassification the inferenceClassification to apply
 * @returns a Promise that handles the service request response
 */
export function updateConversationHashtagsService(
    conversationContexts: ApplyConversationActionContext[],
    hashtag: string,
    hashtagBase: string,
    folderId: string,
    inferenceClassification?: InferenceClassification
): Promise<ApplyConversationActionResponseMessage> {
    const requestBody = configureRequestBody(
        conversationContexts,
        hashtag,
        hashtagBase,
        folderId,
        inferenceClassification
    );

    return applyConversationActionOperation({
        Header: getJsonRequestHeader(),
        Body: requestBody,
    }).then(response => response.Body.ResponseMessages.Items[0]);
}
